// The item bank's home (byj-1wv.51.18): the order the bank is used in, a
// card a step — items, their check and review, blueprints, papers, the exam
// on Share, its grading — each with where it stands now and a link to where
// it is done; then what waits on the person: for an instructor their items
// sent back and what is under way, for an admin the reviews, the requests
// and the failed jobs. What it counts is read from the lists the sections
// read; nothing is counted the server does not say.
import { useEffect, useState } from "react";
import type { BankClient, BankItem, BankJob, BankMe, Blueprint, Paper, PaperRequest } from "../bank";
import { t, whenMinute } from "../i18n";
import { navigate } from "../router";
import { Link } from "./Nav";
import { EmptyRows, Grouped } from "./Rows";
import { jobKindWord } from "./BankJobs";
import { paperOf, stateWord, targetName } from "./BankItems";
import { requestTitle, requestWord } from "./BankPapers";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

type Data = { items: BankItem[]; requests: PaperRequest[]; papers: Paper[]; blueprints: Blueprint[]; jobs: BankJob[] };

/** A version whose next step is its author's: sent back, or its kit never arrived (the items' sentBack, and uploading). */
const asksAuthor = (s?: string) => s === "invalid" || s === "changes_requested" || s === "rejected" || s === "uploading";
const beingChecked = (s?: string) => s === "checking" || s === "in_review";

/** A thing on the home's list: what it is, where it goes, how it stands. */
type Todo = { key: string; mark: string; title: string; sub: string; to: string; chip: string; warn?: boolean };

function TodoRow({ x }: { x: Todo }) {
  return (
    <li className="share-row no-menu is-live" data-testid="bank-todo">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {x.mark}
      </span>
      <div className="row-main">
        <Link to={x.to} className="row-title">
          {x.title}
        </Link>
        <div className="row-sub">{x.sub}</div>
      </div>
      <div className="row-meta">
        <span className={`chip${x.warn ? " chip-warn" : ""}`}>{x.chip}</span>
      </div>
    </li>
  );
}

/** A step of the bank's use: its number, its name, where it happens, where it stands, and its link. */
function Step({ n, title, place, now, to, testid }: { n: number; title: string; place: "bank" | "share"; now: string; to: string; testid: string }) {
  return (
    <li className={`bank-home-step is-${place}`} data-testid={testid}>
      <Link to={to} className="bank-home-step-link">
        <span className="bank-map-head">
          <span className="stage-mark" aria-hidden="true">
            {n}
          </span>
          <strong>{title}</strong>
          <span className="chip bank-map-place">{t(`bank:guidePlace.${place}`)}</span>
        </span>
        <span className="small bank-home-now">{now}</span>
      </Link>
    </li>
  );
}

export function BankHome({ client, me }: { client: BankClient; me: BankMe }) {
  const admin = me.role === "admin";
  const [d, setD] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const [items, requests, papers, blueprints, jobs] = await Promise.all([
          client.items().then((r) => r.items),
          client.requests(admin),
          client.papers(admin),
          client.blueprints(),
          admin ? client.jobs() : Promise.resolve([] as BankJob[]),
        ]);
        setD({ items, requests, papers, blueprints, jobs });
      } catch (err) {
        setError(errText(err));
      }
    })();
  }, [client, admin]);
  if (!d) return error ? <p className="error">{t("common:error", { m: error })}</p> : <p className="muted">{t("common:loading")}</p>;

  const mine = d.items.filter((x) => x.mine);
  const titleOf = Object.fromEntries(d.blueprints.map((b) => [b.id, b.title]));
  const names = Object.fromEntries(d.items.map((x) => [x.id, x.title || x.id]));
  const pending = d.requests.filter((r) => r.state === "pending");
  const published = d.blueprints.filter((b) => b.published > 0).length;
  const reviewing = d.items.filter((x) => x.latest_state === "in_review");
  const failed = d.jobs.filter((j) => j.state === "failed");

  // the steps, each where it stands now for this person
  const steps = [
    { title: t("bank:homeStep.items"), place: "bank" as const, to: "/bank/items",
      now: admin ? t("bank:homeNow.itemsAdmin", { count: d.items.length, active: d.items.filter((x) => x.status === "active").length }) : t("bank:homeNow.itemsUser", { n: mine.length }) },
    { title: t("bank:homeStep.review"), place: "bank" as const, to: "/bank/items",
      now: admin ? t("bank:homeNow.reviewAdmin", { n: reviewing.length }) : t("bank:homeNow.reviewUser", { back: mine.filter((x) => asksAuthor(x.latest_state)).length, on: mine.filter((x) => beingChecked(x.latest_state)).length }) },
    { title: t("bank:homeStep.blueprint"), place: "bank" as const, to: admin ? "/bank/blueprints" : "/bank/request",
      now: t("bank:homeNow.blueprint", { n: published }) },
    { title: t("bank:homeStep.paper"), place: "bank" as const, to: "/bank/papers",
      now: t("bank:homeNow.paper", { count: d.requests.length, pending: pending.length }) },
    { title: t("bank:homeStep.exam"), place: "share" as const, to: "/exams", now: t("bank:homeNow.exam", { count: d.papers.length }) },
    { title: t("bank:homeStep.grade"), place: "share" as const, to: "/exams", now: t("bank:homeNow.grade") },
  ];

  // what is the person's to do now, and what is under way that they wait on
  const todo: Todo[] = admin
    ? [
        ...reviewing.map((x) => ({ key: `r${x.id}`, mark: "itm", title: x.title || x.id, sub: t("bank:todo.review", { v: x.latest, email: x.author_email ?? "" }), to: `/bank/items/${encodeURIComponent(x.id)}`, chip: stateWord("in_review") })),
        ...pending.map((r) => ({ key: `q${r.id}`, mark: "ppr", title: requestTitle(r, titleOf), sub: t("bank:todo.decide", { klass: r.class_name, email: r.requester_email, w: whenMinute(r.created_at) }), to: "/bank/papers", chip: requestWord("pending") })),
        ...failed.map((j) => ({ key: `j${j.id}`, mark: "job", title: `${jobKindWord(j.kind)} · ${targetName(j.target, names, paperOf(j)) ?? j.target}`, sub: j.error || whenMinute(j.created_at), to: "/bank/jobs", chip: t("bank:jobState.failed"), warn: true })),
      ]
    : mine
        .filter((x) => asksAuthor(x.latest_state))
        .map((x) => ({ key: `b${x.id}`, mark: "itm", title: x.title || x.id, sub: t("bank:todo.fix", { v: x.latest }), to: `/bank/items/${encodeURIComponent(x.id)}`, chip: stateWord(x.latest_state!), warn: true }));
  const waiting: Todo[] = admin
    ? []
    : [
        ...mine.filter((x) => beingChecked(x.latest_state)).map((x) => ({ key: `c${x.id}`, mark: "itm", title: x.title || x.id, sub: t("bank:todo.checking", { v: x.latest }), to: `/bank/items/${encodeURIComponent(x.id)}`, chip: stateWord(x.latest_state!) })),
        ...d.requests
          .filter((r) => r.state === "pending" || r.state === "drawing")
          .map((r) => ({ key: `w${r.id}`, mark: "ppr", title: requestTitle(r, titleOf), sub: t("bank:todo.requested", { klass: r.class_name, w: whenMinute(r.created_at) }), to: "/bank/papers", chip: requestWord(r.state) })),
      ];

  return (
    <div className="stack bank-home" data-testid="bank-home">
      <div className="panel-head">
        <p className="muted small panel-lead">{admin ? t("bank:homeLeadAdmin") : t("bank:homeLeadUser")}</p>
        <span className="row wrap">
          <button onClick={() => navigate("/bank/submit")}>{t("bank:navSubmit")}</button>
          <button className="primary" onClick={() => navigate("/bank/request")}>
            {t("bank:reqTitle")}
          </button>
        </span>
      </div>
      <ol className="bank-home-flow" aria-label={t("bank:guideFlowLabel")}>
        {steps.map((s, i) => (
          <Step key={s.title} n={i + 1} title={s.title} place={s.place} now={s.now} to={s.to} testid={`bank-home-step-${i + 1}`} />
        ))}
      </ol>
      <Grouped<Todo>
        id="bank-home"
        headed
        groups={[
          { title: t("bank:homeTodo"), key: "todo", className: "rows-head-attention", items: todo, none: <EmptyRows text={t("bank:homeTodoNone")} /> },
          ...(admin ? [] : [{ title: t("bank:homeWaiting"), key: "waiting", className: "rows-head-live", items: waiting }]),
        ]}
        row={(x) => <TodoRow key={x.key} x={x} />}
      />
    </div>
  );
}
